<script setup lang="ts">
import InformationBaseCard from '@/components/Cards/InformationBaseCard.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import PartnersSelector from '@/components/Selector/components/PartnersSelector.vue'
import type { ExtendedPrimitives } from '@/types/ExtendedPrimitives'
import type { EventModel } from '@/types/models/EventModel'
import type { ConfigType, PartnerConfigModel } from '@/types/models/PartnerConfigModel'
import { EPartnersPerm } from '@/types/permissions/PartnersPermissionsEnum'
import { DATETIME_FORMAT } from '@/utils/dateUtils'
import moment from 'moment'
import { ref, watch } from 'vue'
import TypographyItem from '../../../components/Typography/TypographyItem.vue'
import type { PartnerModel } from '../../../types/partners/PartnerModel'
import EventActions from './components/EventActions.vue'
import EventInformations from './components/EventInformations.vue'
import EventPartnerConfig from './components/EventPartnerConfig.vue'

type Props = {
    data: EventModel
    partnerConfigData?: PartnerModel
    partnerConfig?: Record<string, ConfigType | string>
    partnerConfigValues?: Record<string, ExtendedPrimitives>
    isInEditMode: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'update', value: Partial<EventModel>): void
    (e: 'selectPartner', value: Partial<EventModel>): void
    (e: 'updatePartnerConfig', value: Partial<PartnerConfigModel>): void
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const changedPartner = ref(false)
const handleChangePartner = (partner: Partial<EventModel>) => {
    changedPartner.value = true
    emit('selectPartner', partner)
}

watch(
    () => props.data.partner?.id,
    () => (changedPartner.value = false)
)
</script>

<template>
    <StackLayout direction="column" :gap="16" class="full-width">
        <StackLayout :gap="8" isResponsive class="full-width">
            <StackLayout direction="column" :gap="12" class="full-width">
                <StackLayout :gap="8">
                    <EventInformations
                        :data="data"
                        @update="$emit('update', $event)"
                        :isInEditMode="isInEditMode" />
                    <StackLayout direction="column" :gap="8">
                        <EventActions :data="data" @refresh="$emit('refresh')" />
                        <InformationBaseCard
                            v-if="data?.guntimes?.length"
                            :label="$t('event.detail.informations.guntimes')">
                            <StackLayout direction="column" :gap="4">
                                <TypographyItem
                                    v-for="(item, key) in data.guntimes"
                                    :key="key"
                                    :label="`${item.name} : ${moment(item.start_at)
                                        .format(DATETIME_FORMAT)
                                        .toString()}`" />
                            </StackLayout>
                        </InformationBaseCard>
                    </StackLayout>
                </StackLayout>

                <InformationBaseCard
                    v-if="isInEditMode"
                    :label="$t('event.detail.informations.partnerConfigUpdate')">
                    <PermissionWrapper :permission="EPartnersPerm.PARTNERS_VIEW">
                        <PartnersSelector
                            :partner_config="partnerConfigValues"
                            :partnerId="data.partner_id"
                            @update="handleChangePartner"
                            class="full-width" />
                    </PermissionWrapper>
                </InformationBaseCard>
                <EventPartnerConfig
                    v-if="!changedPartner && (partnerConfig || partnerConfigValues)"
                    :partnerConfigData="partnerConfigData"
                    :partnerConfig="partnerConfig"
                    :partnerConfigValues="partnerConfigValues"
                    :isInEditMode="isInEditMode"
                    @update="$emit('updatePartnerConfig', $event)" />
            </StackLayout>
        </StackLayout>
    </StackLayout>
</template>
